import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type PresenceState = "entering" | "present" | "exiting";

const PresenceStateContext = createContext<PresenceState>("present");

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : { matches: false, addEventListener: () => undefined, removeEventListener: () => undefined };
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

type PresenceProps = {
  open: boolean;
  children?: ReactNode;
  exitDurationMs?: number;
  onExited?: () => void;
};

export function usePresence(
  open: boolean,
  exitDurationMs = 220,
  onExited?: () => void
) {
  const reducedMotion = useReducedMotion();
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<PresenceState>(
    open ? "entering" : "exiting"
  );
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  useEffect(() => {
    if (open) {
      setMounted(true);
      setState(reducedMotion ? "present" : "entering");
      if (reducedMotion) return;
      const frame = typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(() => setState("present"))
        : window.setTimeout(() => setState("present"), 0);
      return () => {
        if (typeof window.cancelAnimationFrame === "function") window.cancelAnimationFrame(frame);
        else window.clearTimeout(frame);
      };
    }

    if (!mounted) return;
    setState("exiting");
    if (reducedMotion) {
      setMounted(false);
      onExitedRef.current?.();
      return;
    }
    const timeout = window.setTimeout(() => {
      setMounted(false);
      onExitedRef.current?.();
    }, exitDurationMs);
    return () => window.clearTimeout(timeout);
  }, [exitDurationMs, mounted, open, reducedMotion]);

  return { isMounted: mounted, state };
}

/** Keeps the last rendered child mounted while its CSS exit state is visible. */
export function Presence({
  open,
  children,
  exitDurationMs = 220,
  onExited,
}: PresenceProps) {
  const { isMounted: mounted, state } = usePresence(
    open,
    exitDurationMs,
    onExited
  );
  const lastChildRef = useRef<ReactNode>(null);

  if (open && children != null) {
    lastChildRef.current = children;
  }

  if (!mounted) return null;

  return (
    <PresenceStateContext.Provider value={state}>
      {lastChildRef.current}
    </PresenceStateContext.Provider>
  );
}

export function usePresenceState() {
  return useContext(PresenceStateContext);
}

export function presenceStateProps(state: PresenceState) {
  return {
    "data-presence-state": state,
    // Exiting surfaces can still contain the element that triggered the close.
    // aria-hidden on that ancestor produces a browser accessibility violation
    // while focus is still inside it; inert also prevents further interaction.
    inert: state === "exiting" ? true : undefined,
  } as const;
}
